import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  Search,
  Folder,
  Share2,
  BookOpen,
  Link2,
  Plus,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  KnowledgeBaseWithDocuments,
  KnowledgeBaseDocument,
  KnowledgeBaseFolder,
} from "../../../../../types/navigationTypes";
import { documentCacheService } from "../../../../../shared/services/documentCacheService";
import { configService } from "../../../../../shared/services/configService";
import { emitKbDocumentsChanged } from "../../../../projects/services/executionTemplatesEvents";
import { useOrganization } from "../../../../../providers/organization-provider";
import { KB_ROOT_UPLOAD_FOLDER_KEY } from "../../../kbUiConstants";
import {
  createKnowledgeBaseForExecution,
  fetchExecutionSharedKnowledgeBaseIds,
  fetchOrganizationSharedKnowledgeBases,
  syncExecutionSharedKnowledgeBaseLinks,
} from "../../../services/knowledgeBaseService";
import { getFoldersForKnowledgeBase, uploadDocumentToKnowledgeBase } from "../../../services/documentUploadService";
import {
  DocumentUploadModal,
  CategoryCreateModal,
  DocumentViewer,
  KnowledgeBaseHeader,
  KnowledgeBaseSelectionSection,
} from "../../molecules";
import { ChatBlockedState } from "../../../../chat/components/molecules/ChatBlockedState";
import { TaskpanePanelShell, PanelLoadingState, PanelEmptyState } from "@/taskpane/shared/components/panel-layout";
import KnowledgeBaseItem from "../KnowledgeBaseItem";

interface KnowledgeBasePanelProps {
  executionId?: string;
  workpillId?: string;
  onOpenProjectSelector?: () => void;
}

const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({
  executionId,
  workpillId,
  onOpenProjectSelector,
}) => {
  const { organizationId, loading: orgLoading } = useOrganization();
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseWithDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedKB, setExpandedKB] = useState<Set<string>>(new Set());
  const [viewingDocument, setViewingDocument] = useState<KnowledgeBaseDocument | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'project' | 'shared'>('project');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedKBForUpload, setSelectedKBForUpload] = useState<string | null>(null);
  const [selectedCategoryForUpload, setSelectedCategoryForUpload] = useState<string | null>(null);
  const [initialFilesForUpload, setInitialFilesForUpload] = useState<File[] | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedKBForCategory, setSelectedKBForCategory] = useState<string | null>(null);
  // Folders map by knowledge base ID
  const [foldersByKB, setFoldersByKB] = useState<Record<string, KnowledgeBaseFolder[]>>({});

  const [createKbOpen, setCreateKbOpen] = useState(false);
  const [linkSharedOpen, setLinkSharedOpen] = useState(false);
  const [kbName, setKbName] = useState("");
  const [kbDescription, setKbDescription] = useState("");
  const [creatingKb, setCreatingKb] = useState(false);
  const [savingSharedLinks, setSavingSharedLinks] = useState(false);
  const [orgSharedKbs, setOrgSharedKbs] = useState<{ id: string; name: string; type: string | null }[]>([]);
  const [loadingOrgShared, setLoadingOrgShared] = useState(false);
  const [draftSharedKbIds, setDraftSharedKbIds] = useState<string[]>([]);

  const cacheWorkpillSegment = workpillId ?? "";

  const invalidateKbCache = useCallback(() => {
    if (!executionId) return;
    documentCacheService.invalidateCache(executionId, cacheWorkpillSegment);
  }, [executionId, cacheWorkpillSegment]);

  // Separar KBs según is_global del modelo de datos:
  // - is_global = true: KB compartida (shared)
  // - is_global = false/null: KB específica de proyecto (project)
  const projectKBs = useMemo(() => 
    knowledgeBases.filter(kb => kb.is_global !== true),
    [knowledgeBases]
  );
  
  const sharedKBs = useMemo(() => 
    knowledgeBases.filter(kb => kb.is_global === true),
    [knowledgeBases]
  );

  // Knowledge bases filtradas según la tab activa
  const filteredKnowledgeBasesByTab = useMemo(() => {
    return activeTab === 'project' ? projectKBs : sharedKBs;
  }, [activeTab, projectKBs, sharedKBs]);

  const filterKnowledgeBases = (
    kbList: KnowledgeBaseWithDocuments[],
    searchTerm: string
  ): KnowledgeBaseWithDocuments[] => {
    if (!searchTerm.trim()) {
      return kbList;
    }
    
    const lowerSearch = searchTerm.toLowerCase();
    
    return kbList
      .map(kb => {
        // Filter documents within each KB
        const filteredDocs = kb.documents.filter(doc => 
          doc.name.toLowerCase().includes(lowerSearch) ||
          doc.parent_name?.toLowerCase().includes(lowerSearch)
        );
        
        // Check if any KB folder matches the search
        const kbFolders = foldersByKB[kb.id] || [];
        const folderMatches = kbFolders.some(folder => 
          folder.name.toLowerCase().includes(lowerSearch)
        );
        
        // Include the KB if it matches the name or description, or if it has matching documents, or if it has matching folders
        const kbMatches = 
          kb.name.toLowerCase().includes(lowerSearch) ||
          kb.description?.toLowerCase().includes(lowerSearch) ||
          filteredDocs.length > 0 ||
          folderMatches;
        
        if (kbMatches) {
          return {
            ...kb,
            documents: filteredDocs
          };
        }
        
        return null;
      })
      .filter((kb): kb is KnowledgeBaseWithDocuments => kb !== null);
  };

  useEffect(() => {
    if (executionId) {
      loadKnowledgeBasesData(true);
    } else {
      setLoading(false);
      setError(null);
      setKnowledgeBases([]);
      setFoldersByKB({});
    }
  }, [executionId, cacheWorkpillSegment]);

  useEffect(() => {
    let cancelled = false;
    if (linkSharedOpen && organizationId) {
      (async () => {
        setLoadingOrgShared(true);
        try {
          const rows = await fetchOrganizationSharedKnowledgeBases(organizationId);
          if (!cancelled) setOrgSharedKbs(rows);
        } finally {
          if (!cancelled) setLoadingOrgShared(false);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [linkSharedOpen, organizationId]);

  useEffect(() => {
    let cancelled = false;
    if (linkSharedOpen && executionId) {
      (async () => {
        const ids = await fetchExecutionSharedKnowledgeBaseIds(executionId);
        if (!cancelled) setDraftSharedKbIds(ids);
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [linkSharedOpen, executionId]);

  // Auto-expand KB when there's an active search
  useEffect(() => {
    if (searchTerm.trim() && knowledgeBases.length > 0) {
      // When there's a search, expand the first KB that has results
      const filtered = filterKnowledgeBases(knowledgeBases, searchTerm);
      if (filtered.length > 0) {
        setExpandedKB(prev => {
          const newSet = new Set(prev);
          newSet.add(filtered[0].id);
          return newSet;
        });
      }
    } else if (!searchTerm.trim() && knowledgeBases.length > 0 && expandedKB.size === 0) {
      // If there's no search and no expanded KB, expand the first one
      setExpandedKB(new Set([knowledgeBases[0].id]));
    }
  }, [searchTerm, knowledgeBases.length]);

  const loadKnowledgeBasesData = async (forceRefresh = false, showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      } else {
        setError(null);
      }
      
      const data = await documentCacheService.getDocuments(
        executionId!,
        cacheWorkpillSegment,
        forceRefresh
      );
      setKnowledgeBases(data);
      
      // Load folders from all KBs in parallel
      const folderPromises = data.map(async (kb) => {
        try {
          const folders = await getFoldersForKnowledgeBase(kb.id);
          return { kbId: kb.id, folders };
        } catch (err) {
          console.error(`Error loading folders for KB ${kb.id}:`, err);
          return { kbId: kb.id, folders: [] };
        }
      });
      
      const folderResults = await Promise.all(folderPromises);
      const foldersMap: Record<string, KnowledgeBaseFolder[]> = {};
      folderResults.forEach(({ kbId, folders }) => {
        foldersMap[kbId] = folders;
      });
      setFoldersByKB(foldersMap);
      
      // Auto-expand the first KB if there's data
      if (data.length > 0) {
        setExpandedKB(new Set([data[0].id]));
      }
    } catch (err) {
      console.error("Error loading knowledge bases:", err);
      if (showLoading) {
        setError("Error loading knowledge bases");
      } else {
        toast.error("Error loading knowledge bases");
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleViewDocument = (doc: KnowledgeBaseDocument) => {
    if (!doc.url) {
      alert('Document URL not available');
      return;
    }
    setViewingDocument(doc);
  };

  const handleCloseViewer = () => {
    setViewingDocument(null);
  };

  const handleDownloadDocument = (doc: KnowledgeBaseDocument) => {
    if (doc.url) {
      const link = document.createElement('a');
      link.href = doc.url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Document URL not available');
    }
  };

  const handleUploadSuccess = (_uploadedDocs?: any) => {
    console.log('[KnowledgeBasePanel] Document uploaded successfully, invalidating cache and reloading data...', {
      executionId,
      workpillId,
      timestamp: new Date().toISOString()
    });
    
    if (executionId) {
      invalidateKbCache();
      loadKnowledgeBasesData(true);
      emitKbDocumentsChanged();
    }
  };

  const handleCategoryCreated = () => {
    console.log('[KnowledgeBasePanel] Category created, invalidating cache and reloading data...', {
      executionId,
      workpillId,
      timestamp: new Date().toISOString(),
    });

    if (executionId) {
      invalidateKbCache();
      loadKnowledgeBasesData(true);
      emitKbDocumentsChanged();
    }
  };

  const uploadFilesWithConcurrency = async (
    files: File[],
    concurrency: number,
    uploadFn: (file: File) => Promise<void>
  ) => {
    const queue = [...files];
    const workers = Array.from({ length: Math.max(1, concurrency) }, async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) return;
        await uploadFn(file);
      }
    });

    await Promise.all(workers);
  };

  const handleUploadFiles = async (kbId: string, categoryName: string, files: FileList, isDirect = false) => {
    // Bloquear en Shared KBs
    if (activeTab === 'shared') {
      toast.info('Shared Knowledge Bases are read-only');
      return;
    }
    
    const kbFolders = foldersByKB[kbId] || [];
    const folder = kbFolders.find(f => f.name === categoryName);
    
    if (isDirect) {
      const filesArray = Array.from(files);

      if (categoryName === KB_ROOT_UPLOAD_FOLDER_KEY) {
        const toastId = toast.loading(
          `Uploading ${filesArray.length} file${filesArray.length === 1 ? "" : "s"}...`
        );
        try {
          await uploadFilesWithConcurrency(filesArray, 3, async (file) => {
            const result = await uploadDocumentToKnowledgeBase({
              file,
              knowledgeBaseId: kbId,
              folderId: null,
              documentName: file.name,
            });
            if (!result.success) {
              throw new Error(result.error || `Error uploading ${file.name}`);
            }
          });
          toast.success("Upload completed", { id: toastId });
          if (executionId) {
            invalidateKbCache();
            loadKnowledgeBasesData(true, false);
            emitKbDocumentsChanged();
          }
        } catch (err) {
          console.error("Error in root upload:", err);
          toast.error(err instanceof Error ? err.message : "Error in direct upload", { id: toastId });
        }
        return;
      }

      if (!folder) {
        toast.error("Folder not found for upload");
        return;
      }

      const toastId = toast.loading(
        `Uploading ${filesArray.length} file${filesArray.length === 1 ? "" : "s"}...`
      );

      try {
        await uploadFilesWithConcurrency(filesArray, 3, async (file) => {
          const result = await uploadDocumentToKnowledgeBase({
            file,
            knowledgeBaseId: kbId,
            folderId: folder.id,
            documentName: file.name
          });

          if (!result.success) {
            throw new Error(result.error || `Error uploading ${file.name}`);
          }
        });
        
        toast.success("Upload completed", { id: toastId });

        if (executionId) {
          invalidateKbCache();
          loadKnowledgeBasesData(true, false);
          emitKbDocumentsChanged();
        }
      } catch (err) {
        console.error("Error in direct upload:", err);
        toast.error(err instanceof Error ? err.message : "Error in direct upload", { id: toastId });
      }
      return;
    }

    if (folder) {
      setSelectedKBForUpload(kbId);
      setSelectedCategoryForUpload(folder.id);
      setInitialFilesForUpload(Array.from(files));
      setShowUploadModal(true);
    } else {
      console.warn(`[KnowledgeBasePanel] Folder ${categoryName} not found in KB ${kbId}`);
      // Fallback to just opening the modal with the KB selected
      setSelectedKBForUpload(kbId);
      setInitialFilesForUpload(Array.from(files));
      setShowUploadModal(true);
    }
  };

  const toggleKB = (kbId: string) => {
    setExpandedKB(prev => {
      const newSet = new Set(prev);
      if (newSet.has(kbId)) {
        newSet.delete(kbId);
      } else {
        newSet.add(kbId);
      }
      return newSet;
    });
  };

  const handleOpenCreateKnowledgeBase = () => {
    if (activeTab === "shared") {
      toast.info("Shared Knowledge Bases are read-only");
      return;
    }
    if (!organizationId) {
      toast.error("Organization is required to create a knowledge base.");
      return;
    }
    setCreateKbOpen(true);
  };

  const handleCreateCategory = (kbIdOrEvent?: string | React.MouseEvent) => {
    // Bloquear en Shared KBs
    if (activeTab === 'shared') {
      toast.info('Shared Knowledge Bases are read-only');
      return;
    }
    
    const kbId = typeof kbIdOrEvent === "string" ? kbIdOrEvent : undefined;
    // Usar la primera KB de la tab activa si no se especifica una
    const firstKBOfTab = filteredKnowledgeBasesByTab[0]?.id;
    const kbToUse = kbId || (expandedKB.size > 0 ? Array.from(expandedKB)[0] : firstKBOfTab);
    console.log('[KnowledgeBasePanel] "Create Folder" button clicked:', {
      kbId,
      expandedKB: Array.from(expandedKB),
      firstKBOfTab,
      kbToUse,
      activeTab,
      timestamp: new Date().toISOString()
    });
    if (kbToUse) {
      setSelectedKBForCategory(kbToUse);
      setShowCategoryModal(true);
    } else {
      console.warn('[KnowledgeBasePanel] No KB found to create folder');
    }
  };

  const handleUploadDocument = () => {
    // Bloquear en Shared KBs
    if (activeTab === 'shared') {
      toast.info('Shared Knowledge Bases are read-only');
      return;
    }

    // Usar la primera KB de la tab activa
    const firstKBOfTab = filteredKnowledgeBasesByTab[0]?.id;
    const kbToUse = expandedKB.size > 0 ? Array.from(expandedKB)[0] : firstKBOfTab;
    if (kbToUse) {
      setSelectedKBForUpload(kbToUse);
      setShowUploadModal(true);
    }
  };

  const handleSubmitCreateKb = async () => {
    if (!executionId || !organizationId) {
      toast.error("Organization is required to create a knowledge base.");
      return;
    }
    const userId = configService.getUserId();
    if (!userId) {
      toast.error("You must be signed in.");
      return;
    }
    const name = kbName.trim();
    if (!name) {
      toast.error("Name is required.");
      return;
    }
    setCreatingKb(true);
    try {
      const id = await createKnowledgeBaseForExecution({
        name,
        description: kbDescription.trim() || undefined,
        executionId,
        userId,
        organizationId,
        is_global: false,
        type: "normal",
      });
      if (!id) {
        toast.error("Could not create knowledge base.");
        return;
      }
      toast.success("Knowledge base created");
      setCreateKbOpen(false);
      setKbName("");
      setKbDescription("");
      setActiveTab("project");
      invalidateKbCache();
      await loadKnowledgeBasesData(true);
      emitKbDocumentsChanged();
    } finally {
      setCreatingKb(false);
    }
  };

  const handleSaveSharedKbLinks = async () => {
    if (!executionId) return;
    setSavingSharedLinks(true);
    try {
      const { error } = await syncExecutionSharedKnowledgeBaseLinks(executionId, draftSharedKbIds);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Shared knowledge bases updated");
      setLinkSharedOpen(false);
      invalidateKbCache();
      await loadKnowledgeBasesData(true);
      emitKbDocumentsChanged();
    } finally {
      setSavingSharedLinks(false);
    }
  };

  const managementModals = executionId ? (
    <>
      <Dialog
        open={createKbOpen}
        onOpenChange={(open) => {
          setCreateKbOpen(open);
          if (!open) {
            setKbName("");
            setKbDescription("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              New knowledge base
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Create a project knowledge base for this execution. You can upload documents after it is created.
          </p>
          <div className="space-y-3 pt-2">
            <div>
              <Label htmlFor="kb-name-addin" className="text-xs">
                Name
              </Label>
              <Input
                id="kb-name-addin"
                value={kbName}
                onChange={(e) => setKbName(e.target.value)}
                placeholder="e.g. Project documentation"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="kb-desc-addin" className="text-xs">
                Description (optional)
              </Label>
              <Textarea
                id="kb-desc-addin"
                value={kbDescription}
                onChange={(e) => setKbDescription(e.target.value)}
                placeholder="Optional"
                rows={3}
                className="mt-1 resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateKbOpen(false)}
              disabled={creatingKb}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmitCreateKb()}
              disabled={creatingKb || orgLoading || !organizationId}
            >
              {creatingKb ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating…
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkSharedOpen} onOpenChange={setLinkSharedOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link shared knowledge bases
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Choose organization shared bases for this project. They stay read-only here; manage files from the web app if needed.
          </p>
          <KnowledgeBaseSelectionSection
            knowledgeBases={orgSharedKbs.map((kb) => ({
              id: kb.id,
              name: kb.name,
              type: (kb.type as "normal" | undefined) ?? "normal",
            }))}
            isLoading={loadingOrgShared}
            selectedIds={draftSharedKbIds}
            onSelectedIdsChange={setDraftSharedKbIds}
            hideTitle
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setLinkSharedOpen(false)} disabled={savingSharedLinks}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveSharedKbLinks()} disabled={savingSharedLinks}>
              {savingSharedLinks ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  ) : null;

  if (loading) {
    return (
      <TaskpanePanelShell>
        <PanelLoadingState message="Loading knowledge bases…" />
      </TaskpanePanelShell>
    );
  }

  if (error) {
    return (
      <TaskpanePanelShell>
        <div className="flex flex-col items-center justify-center gap-3 p-12">
          <AlertTriangle className="h-8 w-8 text-red-500" />
          <p className="m-0 text-center text-xs text-slate-600">{error}</p>
          <Button onClick={() => loadKnowledgeBasesData()} className="mt-2" size="sm" variant="outline">
            Retry
          </Button>
        </div>
      </TaskpanePanelShell>
    );
  }

  if (!executionId) {
    if (onOpenProjectSelector) {
      return (
        <>
          <TaskpanePanelShell variant="flex" className="items-center justify-center !p-4">
            <ChatBlockedState onOpenSelector={onOpenProjectSelector} />
          </TaskpanePanelShell>
          {managementModals}
        </>
      );
    }
    return (
      <>
        <TaskpanePanelShell>
          <PanelEmptyState
            icon={BookOpen}
            title="No project selected"
            description="Select a project to view and manage knowledge bases."
            compact
          />
        </TaskpanePanelShell>
        {managementModals}
      </>
    );
  }

  if (knowledgeBases.length === 0) {
    return (
      <>
        <TaskpanePanelShell className="flex flex-col items-center justify-center">
          <div className="max-w-sm w-full space-y-4 text-center">
            <div className="flex justify-center text-primary/80">
              <BookOpen className="h-12 w-12 opacity-70" />
            </div>
            <h3 className="text-sm font-semibold text-slate-800 m-0">No knowledge bases linked</h3>
            <p className="text-xs text-slate-500 m-0">
              Link shared bases from your organization or create a project knowledge base for this execution.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                type="button"
                size="sm"
                className="w-full gap-2"
                onClick={() => setCreateKbOpen(true)}
                disabled={orgLoading || !organizationId}
              >
                <Plus className="h-4 w-4" />
                Create project knowledge base
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full gap-2"
                onClick={() => setLinkSharedOpen(true)}
                disabled={orgLoading || !organizationId}
              >
                <Link2 className="h-4 w-4" />
                Link shared knowledge bases
              </Button>
            </div>
          </div>
        </TaskpanePanelShell>
        {managementModals}
      </>
    );
  }

  // Si hay KBs pero no hay de la tab seleccionada
  if (filteredKnowledgeBasesByTab.length === 0) {
    return (
      <TaskpanePanelShell>
        {/* Header */}
        <KnowledgeBaseHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          knowledgeBases={knowledgeBases}
          projectKBs={projectKBs}
          sharedKBs={sharedKBs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onCreateKnowledgeBase={handleOpenCreateKnowledgeBase}
          onUploadDocument={handleUploadDocument}
          isReadOnly={activeTab === 'shared'}
        />
        
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="p-4 bg-slate-100 rounded-full mb-4">
            {activeTab === 'project' ? (
              <Folder className="w-8 h-8 text-slate-400" />
            ) : (
              <Share2 className="w-8 h-8 text-slate-400" />
            )}
          </div>
          <p className="m-0 text-sm text-slate-500 text-center">
            {activeTab === 'project' 
              ? 'No project knowledge bases available'
              : 'No shared knowledge bases available'
            }
          </p>
          <p className="m-0 text-xs text-slate-400 text-center mt-2 max-w-[250px]">
            {activeTab === 'project'
              ? 'Knowledge bases linked to this specific execution will appear here'
              : 'Shared knowledge bases available across all executions will appear here'
            }
          </p>
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === 'project' ? 'shared' : 'project')}
            className="mt-4 text-xs text-primary hover:underline"
          >
            Switch to {activeTab === 'project' ? 'Shared' : 'Project'} KBs
          </button>
          {activeTab === 'project' ? (
            <Button
              type="button"
              size="sm"
              variant="default"
              className="mt-3 gap-2"
              onClick={() => setCreateKbOpen(true)}
              disabled={orgLoading || !organizationId}
            >
              <Plus className="h-4 w-4" />
              Create project knowledge base
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3 gap-2"
              onClick={() => setLinkSharedOpen(true)}
              disabled={orgLoading || !organizationId}
            >
              <Link2 className="h-4 w-4" />
              Link shared knowledge bases
            </Button>
          )}
        </div>
        {managementModals}
      </TaskpanePanelShell>
    );
  }

  // If there's a document being viewed, show the viewer
  if (viewingDocument) {
    return (
      <>
        <DocumentViewer document={viewingDocument} onClose={handleCloseViewer} />
        {managementModals}
      </>
    );
  }

  // Aplicar filtro de búsqueda sobre las KBs de la tab activa
  const filteredKnowledgeBases = filterKnowledgeBases(filteredKnowledgeBasesByTab, searchTerm);

  return (
    <TaskpanePanelShell>
      {/* Header */}
      <KnowledgeBaseHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        knowledgeBases={knowledgeBases}
        projectKBs={projectKBs}
        sharedKBs={sharedKBs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onCreateKnowledgeBase={handleOpenCreateKnowledgeBase}
        onUploadDocument={handleUploadDocument}
        isReadOnly={activeTab === 'shared'}
      />

      {/* Knowledge Bases List */}
      {filteredKnowledgeBases.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/90 bg-white py-12 px-4 text-center shadow-sm">
          <Search className="mb-3 h-8 w-8 text-slate-300" />
          <p className="m-0 text-sm text-slate-500">
            {searchTerm
              ? `No ${activeTab === "project" ? "project" : "shared"} documents found matching the search`
              : `No ${activeTab === "project" ? "project" : "shared"} knowledge bases available`}
          </p>
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="mt-3 text-xs text-primary hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {filteredKnowledgeBases.map((kb) => (
            <KnowledgeBaseItem
              key={kb.id}
              knowledgeBase={kb}
              folders={foldersByKB[kb.id] || []}
              isExpanded={expandedKB.has(kb.id)}
              onToggle={toggleKB}
              onViewDocument={handleViewDocument}
              onDownloadDocument={handleDownloadDocument}
              onCreateFolder={handleCreateCategory}
              onUploadFiles={handleUploadFiles}
              isReadOnly={activeTab === 'shared'}
            />
          ))}
        </div>
      )}
      
      {/* Document Upload Modal */}
      {knowledgeBases.length > 0 && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedKBForUpload(null);
            setSelectedCategoryForUpload(null);
            setInitialFilesForUpload(null);
          }}
          knowledgeBaseId={selectedKBForUpload || undefined}
          initialFolderId={selectedCategoryForUpload || undefined}
          initialFiles={initialFilesForUpload || undefined}
          knowledgeBases={knowledgeBases}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Category Create Modal */}
      {knowledgeBases.length > 0 && (
        <CategoryCreateModal
          isOpen={showCategoryModal}
          onClose={() => {
            setShowCategoryModal(false);
            setSelectedKBForCategory(null);
          }}
          knowledgeBaseId={selectedKBForCategory || undefined}
          knowledgeBases={knowledgeBases}
          onFolderCreated={handleCategoryCreated}
        />
      )}
      {managementModals}
    </TaskpanePanelShell>
  );
};

export default KnowledgeBasePanel;
